// Layout-group hygiene shared by the renderers and the designer.

export type GroupLike = {
  GroupName?: unknown
  StandardName?: unknown
  COLUMNS?: unknown
}
type ColumnLike = { COLID?: unknown; NAME?: unknown; groupName?: unknown }

const groupKey = (name: unknown) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
const columnKey = (c: ColumnLike) => `${String(c.COLID ?? '')}|${String(c.NAME ?? '').toUpperCase()}`

/**
 * Drop the copies of a column the app server left in a group it MOVED OUT OF.
 *
 * insight-bi's designer shares one column object between groups, so moving a
 * column can leave it listed in the group it came from while its own
 * `groupName` names where it went — a calendar saved that way lists DOC_DATE
 * and DUE_DATE in both Date and First Row Left, and draws every field twice.
 * An entry is dropped only when its `groupName` names ANOTHER group of the
 * same visual that holds the same column; a stale label with no such home is
 * a real placement and is kept. Twasta's own designer MOVES a column and
 * stamps its target group, so a visual built here never matches. Mirrors the
 * import-side `reverse_visual_service._drop_stray_placements`, which cleans a
 * visual as it comes in; this covers copies imported before it existed.
 * Returns new group objects; the input is not mutated.
 */
export function dropMovedColumnCopies<G extends GroupLike>(groups: G[]): G[] {
  if (!Array.isArray(groups)) return groups
  const byName = new Map<string, G>()
  for (const g of groups) {
    for (const n of [g?.GroupName, g?.StandardName]) {
      const k = groupKey(n)
      if (k && !byName.has(k)) byName.set(k, g)
    }
  }
  return groups.map((g) => {
    const cols = g?.COLUMNS
    if (!Array.isArray(cols) || !cols.length) return g
    const own = new Set([groupKey(g.GroupName), groupKey(g.StandardName)])
    const kept = (cols as ColumnLike[]).filter((c) => {
      const home = c && typeof c === 'object' ? groupKey(c.groupName) : ''
      if (!home || own.has(home)) return true
      const target = byName.get(home)
      if (!target || target === g || !Array.isArray(target.COLUMNS)) return true
      const k = columnKey(c)
      return !(target.COLUMNS as ColumnLike[]).some((x) => x && columnKey(x) === k)
    })
    return kept.length === cols.length ? g : { ...g, COLUMNS: kept }
  })
}
